import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { Field, TextInput } from '../components/Field.tsx';
import { useBranding } from '../lib/branding.tsx';
import { companyInfoLabel } from '../lib/companyInfo.ts';
import type { Company } from '../lib/types.ts';
import { REQUIRED_COMPANY_INFO_FIELDS } from '@l2u/shared/progress';
import { useAbnLookup, type AbnLookupState } from '../lib/useAbnLookup.ts';

type SelfForm = {
  abn: string;
  trustee_info: string;
  primary_contact_name: string;
  contact_role: string;
  contact_phone: string;
  contact_email: string;
  employee_count: string;
  legal_entity_name: string;
  address_state: string;
  address_postcode: string;
  suburb: string;
};

function toSelfForm(c: Company): SelfForm {
  return {
    abn: c.abn ?? '',
    trustee_info: c.trustee_info ?? '',
    primary_contact_name: c.primary_contact_name ?? '',
    contact_role: c.contact_role ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_email: c.contact_email ?? '',
    employee_count: c.employee_count === null ? '' : String(c.employee_count),
    legal_entity_name: c.legal_entity_name ?? '',
    address_state: c.address_state ?? '',
    address_postcode: c.address_postcode ?? '',
    suburb: c.suburb ?? '',
  };
}

export function CompanyInformation() {
  const qc = useQueryClient();
  const { branding } = useBranding();

  const { data, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => companyApi.get<{ company: Company }>('/api/me'),
  });

  const [form, setForm] = useState<SelfForm | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data?.company) setForm(toSelfForm(data.company));
  }, [data?.company.id, data?.company.updated_at]);

  const company = data?.company;
  const locked = company?.status === 'review' || company?.status === 'compliant';

  const missing = useMemo(() => {
    if (!company) return [] as string[];
    const result: string[] = [];
    if (!company.abn) result.push('abn');
    if (!company.primary_contact_name) result.push('primary_contact_name');
    if (!company.contact_role) result.push('contact_role');
    if (!company.contact_phone) result.push('contact_phone');
    if (!company.contact_email) result.push('contact_email');
    if (company.employee_count === null) result.push('employee_count');
    return result;
  }, [company]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('Form not ready');
      const payload: Record<string, unknown> = {
        abn: form.abn.trim() || null,
        trustee_info: form.trustee_info.trim() || null,
        primary_contact_name: form.primary_contact_name.trim() || null,
        contact_role: form.contact_role.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        legal_entity_name: form.legal_entity_name.trim() || null,
        address_state: form.address_state.trim() || null,
        address_postcode: form.address_postcode.trim() || null,
        suburb: form.suburb.trim() || null,
      };
      const empCount = form.employee_count.trim();
      if (empCount === '') {
        payload.employee_count = null;
      } else {
        const n = Number.parseInt(empCount, 10);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error('# of employees must be a non-negative number');
        }
        payload.employee_count = n;
      }
      return companyApi.patch<{ company: Company }>('/api/me', payload);
    },
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) => setError(err),
  });

  const post = useCallback(<T,>(p: string, b?: unknown) => companyApi.post<T>(p, b), []);
  const abnLookup = useAbnLookup(form?.abn ?? '', post, '/api/me/abn-lookup');
  const lastFilled = useRef<string | null>(null);
  useEffect(() => {
    if (abnLookup.status !== 'done' || !abnLookup.result.ok) return;
    const r = abnLookup.result;
    if (lastFilled.current === r.abn) return;
    lastFilled.current = r.abn;
    setForm((f) => (f ? {
      ...f,
      legal_entity_name: r.legalEntityName ?? f.legal_entity_name,
      address_state: r.addressState ?? f.address_state,
      address_postcode: r.addressPostcode ?? f.address_postcode,
    } : f));
  }, [abnLookup]);

  if (isLoading || !form || !company) return <CenteredSpinner />;

  function set<K extends keyof SelfForm>(key: K, value: SelfForm[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Company information</h1>
        <p className="mt-1 text-sm text-slate-500">
          Review the details we have on file and fill in the remaining fields. Required fields must
          be completed before you can submit the questionnaire.
        </p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Pre-filled by your administrator
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          If anything is wrong here, contact your administrator.
        </p>
        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ReadOnlyRow label="Company legal name" value={company.company_name} />
          <ReadOnlyRow label="Email address" value={company.email} />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'contractorType')}
            value={company.contractor_type}
          />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'companyNumber')}
            value={company.company_number}
          />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'contractNumber')}
            value={company.contract_number}
          />
          <ReadOnlyRow
            label={companyInfoLabel(branding, 'teamRegion')}
            value={company.team_region}
          />
        </dl>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            About your company
          </h2>
          <div className="flex items-center gap-2">
            {savedAt && <span className="text-xs text-emerald-600">Saved</span>}
            <Btn
              size="sm"
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={locked}
            >
              Save
            </Btn>
          </div>
        </div>

        {locked && (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Submitted — fields are locked for review.
          </div>
        )}

        {missing.length > 0 && !locked && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {missing.length} required field{missing.length === 1 ? '' : 's'} remaining before you
            can submit the questionnaire.
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="ABN" required>
            <TextInput
              value={form.abn}
              disabled={locked}
              onChange={(e) => set('abn', e.target.value)}
            />
            <AbnStatusNote state={abnLookup} />
          </Field>
          <Field label="Legal entity name" hint="Auto-filled from the ABN registry">
            <TextInput value={form.legal_entity_name} disabled={locked} onChange={(e) => set('legal_entity_name', e.target.value)} />
          </Field>
          <Field label="Suburb">
            <TextInput value={form.suburb} disabled={locked} onChange={(e) => set('suburb', e.target.value)} />
          </Field>
          <Field label="State"><TextInput value={form.address_state} disabled={locked} onChange={(e) => set('address_state', e.target.value)} /></Field>
          <Field label="Postcode"><TextInput value={form.address_postcode} disabled={locked} onChange={(e) => set('address_postcode', e.target.value)} /></Field>
          <Field
            label="Trustee Name and ACN (if applicable)"
            hint="Only required if you operate under a trust."
          >
            <TextInput
              value={form.trustee_info}
              disabled={locked}
              onChange={(e) => set('trustee_info', e.target.value)}
            />
          </Field>
          <Field label={companyInfoLabel(branding, 'primaryContact')} required>
            <TextInput
              value={form.primary_contact_name}
              disabled={locked}
              onChange={(e) => set('primary_contact_name', e.target.value)}
            />
          </Field>
          <Field label="Contact role/position" required>
            <TextInput
              value={form.contact_role}
              disabled={locked}
              onChange={(e) => set('contact_role', e.target.value)}
            />
          </Field>
          <Field label="Contact phone number" required>
            <TextInput
              type="tel"
              value={form.contact_phone}
              disabled={locked}
              onChange={(e) => set('contact_phone', e.target.value)}
            />
          </Field>
          <Field label="Contact email address" required>
            <TextInput
              type="email"
              value={form.contact_email}
              disabled={locked}
              onChange={(e) => set('contact_email', e.target.value)}
            />
          </Field>
          <Field label="# of employees" required>
            <TextInput
              type="number"
              min={0}
              value={form.employee_count}
              disabled={locked}
              onChange={(e) => set('employee_count', e.target.value)}
            />
          </Field>
        </div>
        <ErrorMessage error={error} />
      </section>
    </div>
  );
}

export function AbnStatusNote({ state }: { state: AbnLookupState }) {
  if (state.status === 'loading') return <p className="mt-1 text-xs text-slate-500">Checking ABN…</p>;
  if (state.status !== 'done') return null;
  const r = state.result;
  if (r.ok && r.verified) return <p className="mt-1 text-xs text-emerald-600">✓ Verified{r.legalEntityName ? ` — ${r.legalEntityName}` : ''}</p>;
  if (r.ok) return <p className="mt-1 text-xs text-amber-600">✗ Not verified{r.abnStatus ? ` (${r.abnStatus})` : ''}</p>;
  if (r.reason === 'not_configured') return <p className="mt-1 text-xs text-slate-400">ABN lookup not configured</p>;
  return <p className="mt-1 text-xs text-amber-600">Couldn't verify ABN</p>;
}

function ReadOnlyRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">
        {value && value.trim().length > 0 ? (
          value
        ) : (
          <span className="italic text-slate-400">Not provided</span>
        )}
      </dd>
    </div>
  );
}

// Re-export so other modules can reference the same constant if needed.
export { REQUIRED_COMPANY_INFO_FIELDS };
